import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Smartphone, Download, Share, Plus, X, Sparkles, Check } from "lucide-react";

export default function PWAInstallModal() {
  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);
  const [isIOS, setIsIOS] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [showiOSGuide, setShowiOSGuide] = React.useState(false);
  const [isInstalled, setIsInstalled] = React.useState(false);

  React.useEffect(() => {
    // Check if already in standalone PWA mode
    const isStandalone = 
      window.matchMedia("(display-mode: standalone)").matches || 
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (within 24 hours)
    let dismissedTime = null;
    try {
      dismissedTime = localStorage.getItem("pwa-install-dismissed-time");
    } catch (e) {}
    if (dismissedTime) {
      const parsedTime = parseInt(dismissedTime, 10);
      const isPast24Hours = Date.now() - parsedTime > 1 * 24 * 60 * 60 * 1000;
      if (!isPast24Hours) {
        return; // Don't show if dismissed within 24 hours
      }
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // Listen for beforeinstallprompt event (Android / Desktop Chrome / Edge)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Wait slightly after loading to invite installation gracefully
      setTimeout(() => {
        setShowModal(true);
      }, 4000);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // If iOS Safari and not standalone, show the prompt to guide them!
    if (isIosDevice && !isStandalone) {
      const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios|opera|opt/.test(userAgent);
      if (isSafari) {
        setTimeout(() => {
          setShowModal(true);
        }, 5000);
      }
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        console.log("PWA installation accepted by user");
        setIsInstalled(true);
        setShowModal(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowiOSGuide(true);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem("pwa-install-dismissed-time", Date.now().toString());
    } catch (e) {}
    setShowModal(false);
  };

  if (isInstalled || !showModal) return null;

  return (
    <AnimatePresence>
      <div id="pwa-root-container" className="fixed inset-x-0 bottom-0 z-50 p-4 pb-6 md:p-6 flex justify-center pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 260, damping: 25 }}
          className="w-full max-w-md bg-white rounded-3xl shadow-[0_24px_50px_-12px_rgba(120,68,9,0.18)] border border-amber-100 p-5 pointer-events-auto"
        >
          {!showiOSGuide ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 shadow-inner">
                    <Smartphone className="h-6 w-6 stroke-[1.8]" />
                  </div>
                  <div>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full mb-1">
                      <Sparkles className="h-3 w-3 animate-pulse" /> Aplikasi PWA
                    </span>
                    <h3 className="font-bold text-stone-800 text-base leading-tight">
                      Pasang Satu Wajan di HP
                    </h3>
                  </div>
                </div>
                <button
                  onClick={handleDismiss}
                  className="h-8 w-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <p className="text-stone-500 text-xs leading-relaxed">
                Akses asisten dapur MPASI & belanja keluarga jauh lebih cepat lewat layar utama HP-mu, hemat kuota dan bisa dibuka layaknya aplikasi asli!
              </p>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-3 text-stone-500 hover:text-stone-700 font-bold text-xs rounded-2xl bg-stone-50 hover:bg-stone-100 transition-all border border-stone-100"
                >
                  Nanti Saja
                </button>
                <button
                  onClick={handleInstallClick}
                  className="flex-1 py-1.5 px-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-xs rounded-2xl shadow-md border-b-2 border-amber-700 transition-all flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {isIOS ? "Lihat Cara Pasang" : "Pasang Sekarang"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-stone-800 text-base flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-amber-600" />
                  Cara Pasang di iPhone
                </h3>
                <button
                  onClick={() => setShowiOSGuide(false)}
                  className="h-8 w-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-3.5 text-xs text-stone-600">
                <div className="flex gap-3">
                  <div className="h-6 w-6 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-xs shrink-0">
                    1
                  </div>
                  <p className="leading-relaxed">
                    Ketuk tombol <strong className="font-bold text-stone-800 inline-flex items-center gap-0.5 bg-stone-100 px-1.5 py-0.5 rounded">Bagikan <Share className="h-3 w-3 inline" /></strong> di bagian bawah Safari.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="h-6 w-6 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-xs shrink-0">
                    2
                  </div>
                  <p className="leading-relaxed">
                    Gulir ke bawah dan ketuk pilihan <strong className="font-bold text-stone-800 inline-flex items-center gap-0.5 bg-stone-100 px-1.5 py-0.5 rounded">Tambahkan ke Layar Utama <Plus className="h-3 w-3 inline" /></strong> (Add to Home Screen).
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="h-6 w-6 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-xs shrink-0">
                    3
                  </div>
                  <p className="leading-relaxed">
                    Ketuk <strong className="font-bold text-stone-800 bg-stone-100 px-1.5 py-0.5 rounded">Tambah</strong> di kanan atas untuk mengonfirmasi pemasangan.
                  </p>
                </div>
              </div>

              <div className="pt-2 flex gap-2">
                <button
                  onClick={() => setShowiOSGuide(false)}
                  className="flex-1 py-3 text-stone-600 hover:text-stone-800 font-bold text-xs rounded-2xl bg-stone-50 hover:bg-stone-100 transition-all border border-stone-100"
                >
                  Kembali
                </button>
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-3 bg-stone-800 hover:bg-stone-900 text-white font-bold text-xs rounded-2xl transition-all shadow-md"
                >
                  Selesai
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
